import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllPlansThunk } from "../../../../store/thunks/jewelleryPlan/planThunks";
import PlansTable from "./PlansTable";
import PlanCard from "../../../../components/JewelleryPlans/common/PlanCard";
import {
  JEWELLERY_ROUTES,
  PLAN_STATUS,
} from "../../../../constants/jewellery.constant";
import {
  PlusCircleIcon,
  SearchIcon,
  FilterIcon,
  TableIcon,
  ViewGridIcon,
} from "@heroicons/react/outline";

const ActivePlans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    plans = [],
    loading,
    pagination = {},
  } = useSelector((state) => state.jewelleryPlan?.plans || {});
  const { primaryBranch } = useSelector((state) => state.branch);

  const [viewMode, setViewMode] = useState("grid"); // grid or table
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: PLAN_STATUS.ACTIVE,
    branchId: primaryBranch?._id,
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(
      getAllPlansThunk({
        ...filters,
        page: currentPage,
        limit: 12,
        search: searchTerm,
      }),
    );
  }, [dispatch, filters, currentPage, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6 sm:p-6 p-4">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">Active Plans</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Manage and monitor all active savings plans
            </p>
          </div>
          <button
            onClick={() => navigate(JEWELLERY_ROUTES.CREATE_PLAN)}
            className="inline-flex items-center px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
          >
            <PlusCircleIcon className="h-5 w-5 mr-2" />
            New Plan
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-background shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-neutral-dark" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search by plan number, customer name..."
                className="block w-full pl-10 pr-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Filters and View Toggle */}
          <div className="flex items-center space-x-2">
            {/* Filter Dropdown */}
            <div className="relative">
              <button className="inline-flex items-center px-3 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light">
                <FilterIcon className="h-5 w-5 mr-2" />
                Filters
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex border border-neutral-light rounded-md">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-2 text-sm font-medium rounded-l-md
                  ${
                    viewMode === "grid"
                      ? "bg-neutral-light text-text"
                      : "bg-background text-neutral-dark hover:text-text"
                  }`}
              >
                <ViewGridIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-2 text-sm font-medium rounded-r-md border-l border-neutral-light
                  ${
                    viewMode === "table"
                      ? "bg-neutral-light text-text"
                      : "bg-background text-neutral-dark hover:text-text"
                  }`}
              >
                <TableIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Plans Display */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-background shadow rounded-lg p-12 text-center">
          <p className="text-neutral-dark">No active plans found</p>
          <button
            onClick={() => navigate(JEWELLERY_ROUTES.CREATE_PLAN)}
            className="mt-4 inline-flex items-center px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
          >
            <PlusCircleIcon className="h-5 w-5 mr-2" />
            Create First Plan
          </button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <PlanCard key={plan._id} plan={plan} />
          ))}
        </div>
      ) : (
        <PlansTable plans={plans} />
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-background px-4 py-3 flex items-center justify-between border-t border-neutral-light sm:px-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-neutral-light text-sm font-medium rounded-md text-text bg-background hover:bg-neutral-light"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-neutral-light text-sm font-medium rounded-md text-text bg-background hover:bg-neutral-light"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-text">
                Showing page <span className="font-medium">{currentPage}</span>{" "}
                of <span className="font-medium">{pagination.pages}</span> (
                {pagination.total} total plans)
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-light bg-background text-sm font-medium text-neutral-dark hover:bg-neutral-light"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-light bg-background text-sm font-medium text-neutral-dark hover:bg-neutral-light"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivePlans;
